// src/components/web/Header/index.jsx
import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import LanguageSelector from "./LanguageSelector";
import Icon from "@/theme/vectors/bundle";
import { ROUTES } from "@/utils/web/constants";
import { colors } from "@/theme/web-theme";

const Header = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navigationLinks = [
        { name: t("menu.home"), path: ROUTES.HOME },
        { name: t("menu.products"), path: ROUTES.HOME, scrollTo: "product-showcase" },
    ];

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    // Handle scrolling to product section
    const handleProductClick = (e) => {
        e.preventDefault();
        const productSection = document.getElementById("product-showcase");
        if (productSection) {
            productSection.scrollIntoView({ behavior: "smooth" });
        } else {
            // If not on homepage, navigate to homepage and add hash to URL
            window.location.href = ROUTES.HOME + "#product-showcase";
        }
    };

    const renderMobileMenu = () => (
        <Drawer anchor="right" open={mobileMenuOpen} onClose={toggleMobileMenu}>
            <Box
                sx={{
                    width: 250,
                    backgroundColor: colors.secondary.main,
                    color: colors.secondary.contrastText,
                    height: '100%'
                }}
                role="presentation"
                onClick={toggleMobileMenu}
                onKeyDown={toggleMobileMenu}
            >
                <List>
                    {navigationLinks.map((link, index) => (
                        <ListItem
                            button
                            component={index === 1 ? "a" : Link}
                            to={index === 1 ? undefined : link.path}
                            href={index === 1 ? "#" : undefined}
                            onClick={index === 1 ? handleProductClick : undefined}
                            key={link.path + (link.scrollTo || "")}
                            sx={{
                                color: colors.secondary.contrastText,
                                '&:hover': {
                                    backgroundColor: colors.secondary.light,
                                }
                            }}
                        >
                            <ListItemText 
                                primary={link.name.toUpperCase()}
                                primaryTypographyProps={{
                                    sx: { fontWeight: 500 }
                                }}
                            />
                        </ListItem>
                    ))}
                    <ListItem>
                        <LanguageSelector />
                    </ListItem>
                </List>
            </Box>
        </Drawer>
    );

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Logo - Overlapping outside the AppBar */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '25px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1100,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Link to={ROUTES.HOME}>
                    <Box
                        component="img"
                        src="assets/embox-logo.jpg"
                        alt="Embox Logo"
                        sx={{
                            height: isMobile ? '80px' : '100px',
                            width: 'auto',
                            display: 'block',
                            borderRadius: '25px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                            border: `3px solid ${colors.primary.main}`,
                        }}
                    />
                </Link>
            </Box>

            <AppBar
                position="static"
                elevation={1}
                sx={{
                    backgroundColor: colors.secondary.main,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Container>
                    <Toolbar
                        disableGutters
                        sx={{ 
                            justifyContent: "space-between",
                            height: isMobile ? '70px' : '80px'
                        }}
                    >
                        {/* Desktop Navigation - Left */}
                        {!isMobile && (
                            <Box sx={{ display: "flex", gap: 3, ml: 2 }}>
                                {/* Home link */}
                                <Box
                                    component={Link}
                                    to={navigationLinks[0].path}
                                    sx={{
                                        textDecoration: "none",
                                        color: colors.secondary.contrastText,
                                        fontWeight: 500,
                                        "&:hover": { color: colors.primary.main },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {navigationLinks[0].name}
                                </Box>
                                
                                {/* Products link with scroll functionality */}
                                <Box
                                    component="a"
                                    href="#"
                                    onClick={handleProductClick}
                                    sx={{
                                        textDecoration: "none",
                                        color: colors.secondary.contrastText,
                                        fontWeight: 500,
                                        "&:hover": { color: colors.primary.main },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {navigationLinks[1].name}
                                </Box>
                            </Box>
                        )}

                        {/* Spacer for Logo in Center */}
                        <Box sx={{ width: isMobile ? '80px' : '100px' }} />

                        {/* Desktop Navigation - Right - Now only contains language selector */}
                        {!isMobile && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                    mr: 2
                                }}
                            >
                                <LanguageSelector />
                            </Box>
                        )}

                        {/* Mobile Navigation Icon */}
                        {isMobile && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <IconButton
                                    edge="end"
                                    aria-label="menu"
                                    onClick={toggleMobileMenu}
                                    sx={{
                                        color: colors.secondary.contrastText,
                                    }}
                                >
                                    <Icon name="menu" size={24} />
                                </IconButton>
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Menu Drawer */}
            {renderMobileMenu()}
        </Box>
    );
};

export default Header;
